// custParamsRules.js — WP2 client (docs/frontend-validation-port-spec.md §4
// WP2, closing the fourth and final sign-off verdict). Posts GAM/IMA rows
// plus the ACTIVE PROFILE'S cust_params rules to the proxy's
// custParamsRulesCheck port and returns the server-computed, index-aligned
// verdict groups that buildSignOffReport.js used to compute locally via
// validation/validators.jsx's custParamsRulesCheck (browser-only, reading
// getActiveProfile() from localStorage internally).
//
// WHY RULES TRAVEL IN THE BODY: this check is profile-dependent — the
// active app/platform selection lives in the browser, not the proxy. The
// server never resolves a profile itself; the caller (InspectraNetworkView's
// sign-off button) resolves getActiveProfile().custParamRules and passes it
// as a plain request parameter, exactly like `manualOverrides` already
// travels for /proxy/lifecycle. The browser owns the profile; the server
// only ever receives it, never sources it.
//
// Mirrors api/disclosures.js's own request()/PROXY_API_BASE pattern rather
// than importing it — house convention duplicates this per-file rather than
// sharing a helper module.
//
// Route contract (server side owned by proxy-server/apiServer.js, not this
// file):
//   POST /proxy/validation/cust-params-rules
//   body: { rows: CustParamsRow[], rules: CustParamRule[] }
//   response: { results: [{ index, group }] } — index-aligned 1:1 with the
//     posted rows array. `group` is null when nothing was asserted for that
//     row — either no rules were configured (rules.length === 0) or every
//     rule was page/type-filtered out. A null group is a valid, complete
//     answer, NOT a missing field: the asymmetry to respect is "no rules
//     configured" (null group, do not block) vs "the request itself failed"
//     (no results array at all, DOES block a signed report).

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

async function request(path, init = {}) {
  let res;
  try {
    res = await fetch(`${PROXY_API_BASE}/proxy${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init.headers || {}) },
    });
  } catch (e) {
    const err = new Error(`Could not reach the proxy server at ${PROXY_API_BASE}`);
    err.status = 0;
    err.body = null;
    err.cause = e;
    throw err;
  }
  let json = null;
  try { json = await res.json(); } catch { /* empty/non-JSON body */ }
  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `${init.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

// The only fields proxy-server/validation/custParamsRules.js reads: req.url
// (cust_params / IU parsing), req.requestBody / req.requestBodyParsed (IMA
// POST body cust_params), req.requestType (fallback for `type`). Deliberately
// its own whitelist, NOT api/lifecycle.js's LIFECYCLE_ROW_FIELDS — this
// checker never reads visit/page attribution, classification beyond
// requestType, timing, response status, or platform fields the lifecycle
// pipeline projects, so posting the shared list would ship dead weight (and
// couple this route's payload to lifecycle's field guard for no reason).
export const CUST_PARAMS_ROW_FIELDS = ['url', 'requestBody', 'requestBodyParsed', 'requestType'];

export function projectRowForCustParamsRules(row) {
  if (!row || typeof row !== 'object') return row;
  const out = {};
  CUST_PARAMS_ROW_FIELDS.forEach((f) => {
    if (f in row) out[f] = row[f];
  });
  return out;
}

/**
 * postCustParamsRules(rows, rules) -> Promise<{ results: [{ index, group }] }>
 *
 * rows: the caller's row array (per-group GAM/IMA rows; entries may be
 * null/undefined for groups with no GAM row — the route tolerates that and
 * returns a null group in that slot, same alignment guarantee). Only the
 * whitelisted fields are sent over the wire.
 * rules: the active profile's custParamRules array (may be empty — an empty
 * array is a request parameter meaning "nothing configured", not omission).
 *
 * Throws on transport failure or non-2xx response, same contract as
 * postLifecycleGroups/postDisclosures — signed-report callers must not
 * swallow this and silently recompute locally (spec §5 recipe #3).
 */
export async function postCustParamsRules(rows, rules) {
  const projected = (rows || []).map(projectRowForCustParamsRules);
  return request('/validation/cust-params-rules', {
    method: 'POST',
    body: JSON.stringify({ rows: projected, rules: Array.isArray(rules) ? rules : [] }),
  });
}
